import React from "react";

/**
 * Catches any unhandled render error in the tree and shows a friendly panel
 * instead of a blank white page.  The Login crash (React #31 caused by an
 * array being rendered by sonner) previously took down the whole app —
 * this component makes sure that never happens again.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Unknown error" };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-linen text-ink flex items-center justify-center p-8" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-[#E8EAE9] rounded-2xl p-8 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-2">Something went wrong</div>
          <h1 className="font-serif text-2xl text-ink leading-snug">The page hit an unexpected error.</h1>
          <p className="mt-3 text-sm text-ink-mid">
            Please reload the page. If this keeps happening, contact us at{" "}
            <a href="mailto:admin@viyora.co.in" className="underline text-clay">admin@viyora.co.in</a>.
          </p>
          <button
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-pine text-linen px-6 py-2.5 text-sm font-semibold hover:opacity-90"
          >
            Reload page
          </button>
          {this.state.message && (
            <details className="mt-6 text-left text-xs text-ink-soft">
              <summary className="cursor-pointer">Technical details</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words">{String(this.state.message)}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
